import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { makeStyles } from '@mui/styles';
import { FormattedMessage } from 'react-intl';
import { CustomSetting, DropdownOption } from 'types/types';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '65%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(2)
  },
  helperText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: theme.spacing(1.4),
    lineHeight: theme.spacing(2.3),
    marginBottom: theme.spacing(1)
  },
  columnHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(1)
  },
  columnHeader: {
    flex: 1,
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.75)'
  },
  removeSpacer: {
    width: theme.spacing(6)
  },
  rowContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1)
  },
  textField: {
    flex: 1
  },
  dropdown: {
    flex: 1
  },
  inputTextField: {
    fontSize: 13
  },
  removeButton: {
    width: theme.spacing(6),
    marginTop: theme.spacing(0.5)
  },
  addButtonContainer: {
    marginTop: theme.spacing(1),
    alignSelf: 'flex-start'
  }
}));

export type SyncMappingEntry = {
  issueType: string;
  repliconObject: string;
};

export type WbsHierarchyLevel = {
  enabled: boolean;
  hierarchyLevel: number;
  name: string;
  repliconLevel: string;
};

export type WbsSyncSetting = {
  mappings: {
    projectSyncSetting: string[];
    taskSyncSetting: string[];
    subTaskLevel1SyncSetting: string[];
    subTaskLevel2SyncSetting: string[];
  };
  hierarchyLevels: WbsHierarchyLevel[];
};

type SyncMappingSectionProps = {
  inputSetting: CustomSetting;
  initialSelectValue: WbsSyncSetting | undefined;
  setSave: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        string | boolean | string[] | SyncMappingEntry[] | WbsSyncSetting
      >
    >
  >;
};

const emptyEntry = (): SyncMappingEntry => ({
  issueType: '',
  repliconObject: ''
});

const DEFAULT_PROJECT_ENTRY: SyncMappingEntry = {
  issueType: '',
  repliconObject: 'project'
};

// hierarchyLevel saved on each row is derived from the Replicon Object it
// maps to (not from the Jira issue type). Empty/unknown defaults to 0.
const REPLICON_TO_HIERARCHY_LEVEL: Record<string, number> = {
  project: 2,
  task: 1,
  subTaskLevel1: 0,
  subTaskLevel2: -1
};

const entriesToWbs = (entries: SyncMappingEntry[]): WbsSyncSetting => {
  const hierarchyLevels: WbsHierarchyLevel[] = entries.map((entry) => ({
    enabled: Boolean(entry.issueType.trim()) && Boolean(entry.repliconObject),
    hierarchyLevel: REPLICON_TO_HIERARCHY_LEVEL[entry.repliconObject] ?? 0,
    name: entry.issueType.trim(),
    repliconLevel: entry.repliconObject
  }));

  const namesByReplicon = (level: string) =>
    hierarchyLevels
      .filter((h) => h.enabled && h.repliconLevel === level)
      .map((h) => h.name);

  return {
    mappings: {
      projectSyncSetting: namesByReplicon('project'),
      taskSyncSetting: namesByReplicon('task'),
      subTaskLevel1SyncSetting: namesByReplicon('subTaskLevel1'),
      subTaskLevel2SyncSetting: namesByReplicon('subTaskLevel2')
    },
    hierarchyLevels
  };
};

const wbsToEntries = (wbs: WbsSyncSetting | undefined): SyncMappingEntry[] => {
  if (
    !wbs ||
    !Array.isArray(wbs.hierarchyLevels) ||
    wbs.hierarchyLevels.length === 0
  ) {
    return [DEFAULT_PROJECT_ENTRY];
  }
  return wbs.hierarchyLevels.map((h) => ({
    issueType: h.name,
    repliconObject: h.repliconLevel || ''
  }));
};

const normalizeInitial = (
  initial: WbsSyncSetting | undefined
): SyncMappingEntry[] => wbsToEntries(initial);

const shiftSet = (prev: Set<number>, removedIndex: number): Set<number> => {
  const next = new Set<number>();
  prev.forEach((i) => {
    if (i !== removedIndex) next.add(i > removedIndex ? i - 1 : i);
  });
  return next;
};

export const SyncMappingSection: React.FC<SyncMappingSectionProps> = ({
  inputSetting,
  initialSelectValue,
  setSave
}: SyncMappingSectionProps) => {
  const classes = useStyles();
  const id = inputSetting.id as string;
  const dropdownOptions =
    (inputSetting?.dropdownOptions as DropdownOption[] | undefined) ?? [];

  const [entries, setEntries] = useState<SyncMappingEntry[]>(
    normalizeInitial(initialSelectValue)
  );
  const [blurred, setBlurred] = useState<Set<number>>(new Set());
  const [selectBlurred, setSelectBlurred] = useState<Set<number>>(new Set());
  const handleIssueTypeChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setEntries((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], issueType: newValue };
        return next;
      });
    },
    []
  );

  const handleRepliconObjectChange = useCallback(
    (index: number) => (event: { target: { value: unknown } }) => {
      const newValue = event.target.value as string;
      setEntries((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], repliconObject: newValue };
        return next;
      });
    },
    []
  );

  const handleAddMapping = useCallback(() => {
    setEntries((prev) => [
      ...prev,
      prev.length === 0 ? DEFAULT_PROJECT_ENTRY : emptyEntry()
    ]);
  }, []);

  const handleRemove = useCallback(
    (index: number) => () => {
      setEntries((prev) => prev.filter((_, i) => i !== index));
      setBlurred((prev) => shiftSet(prev, index));
      setSelectBlurred((prev) => shiftSet(prev, index));
    },
    []
  );

  const handleIssueTypeBlur = useCallback(
    (index: number) => () => {
      setBlurred((prev) => new Set([...prev, index]));
    },
    []
  );

  const handleSelectBlur = useCallback(
    (index: number) => () => {
      setSelectBlurred((prev) => new Set([...prev, index]));
    },
    []
  );

  useEffect(() => {
    if (!id) return;
    const wbs = entriesToWbs(entries);
    setSave((prev) => ({ ...prev, [id]: wbs }));
  }, [id, entries, setSave, initialSelectValue]);

  const addButtonText = inputSetting?.addSettingName || 'Add Mapping';
  const messageId = 'dagCustomSettings.addMapping';

  return (
    <div className={classes.container} data-testid="sync-mapping-section">
      {inputSetting?.helperTextValue && (
        <div className={classes.helperText}>
          <span>{inputSetting.helperTextValue}</span>
        </div>
      )}
      <div className={classes.columnHeaderRow}>
        <Typography className={classes.columnHeader}>
          <FormattedMessage
            id="dagCustomSettings.syncMapping.jiraIssueType"
            defaultMessage="Jira Issue Type"
          />
        </Typography>
        <Typography className={classes.columnHeader}>
          <FormattedMessage
            id="dagCustomSettings.syncMapping.repliconObject"
            defaultMessage="Replicon Object"
          />
        </Typography>
        <div className={classes.removeSpacer} />
      </div>
      {entries.map((entry, index) => {
        const issueTypeId = `sync-mapping-issuetype-${index}`;
        const repliconHelperId = `sync-mapping-replicon-${index}-helper-text`;
        const repliconHasError =
          selectBlurred.has(index) && !entry.repliconObject;
        return (
          <div className={classes.rowContainer} key={index}>
            <TextField
              id={issueTypeId}
              size="small"
              className={classes.textField}
              inputProps={{ className: classes.inputTextField }}
              value={entry.issueType}
              onChange={handleIssueTypeChange(index)}
              onBlur={handleIssueTypeBlur(index)}
              error={blurred.has(index) && !entry.issueType.trim()}
              helperText={
                blurred.has(index) && !entry.issueType.trim() ? (
                  <FormattedMessage id="dagCustomSettings.syncMapping.requiredField" />
                ) : (
                  <FormattedMessage id="dagCustomSettings.syncMapping.issueTypeHelperText" />
                )
              }
              FormHelperTextProps={{ sx: { marginLeft: 0 } }}
              data-testid={issueTypeId}
            />
            <FormControl
              size="small"
              className={classes.dropdown}
              error={repliconHasError}
            >
              <Select
                value={entry.repliconObject}
                onChange={handleRepliconObjectChange(index)}
                onBlur={handleSelectBlur(index)}
                displayEmpty
                SelectDisplayProps={{
                  'aria-invalid': repliconHasError,
                  'aria-describedby': repliconHelperId
                }}
                inputProps={{
                  'aria-label': 'Replicon Object',
                  'data-testid': `sync-mapping-replicon-${index}`
                }}
              >
                <MenuItem value="" disabled>
                  <em>
                    <FormattedMessage
                      id="dagCustomSettings.syncMapping.selectValue"
                      defaultMessage="Select value"
                    />
                  </em>
                </MenuItem>
                {dropdownOptions.map((option, optIndex) => {
                  const [label, value] = Object.entries(option)[0];
                  return (
                    <MenuItem key={optIndex} value={value as string}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText id={repliconHelperId}>
                {repliconHasError ? (
                  <FormattedMessage id="dagCustomSettings.syncMapping.requiredField" />
                ) : (
                  ' '
                )}
              </FormHelperText>
            </FormControl>
            <IconButton
              className={classes.removeButton}
              color="secondary"
              onClick={handleRemove(index)}
              data-testid={`sync-mapping-remove-${index}`}
              size="small"
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          </div>
        );
      })}
      <div className={classes.addButtonContainer}>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddMapping}
          data-testid="sync-mapping-add-button"
        >
          <Typography>
            <FormattedMessage id={messageId} defaultMessage={addButtonText} />
          </Typography>
        </Button>
      </div>
    </div>
  );
};
